import { array, fn, get } from '@ember/helper';
import { action } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import searchKeypress from 'webapp/modifiers/search-keypress';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonPopupsPopupListComponent from 'webapp/components/common/popups/popup-list';
import FilterButtonComponent from 'webapp/components/filter/button';
import FilterLineItemComponent from 'webapp/components/filter/line-item';
import FilterLineOrButtonComponent from 'webapp/components/filter/line-or-button';
import FormInputSearchComponent from 'webapp/components/form/input-search';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import TagPopupEmptyComponent from 'webapp/components/tag/popup-empty';

import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';
import type { AnyFilter } from 'webapp/models-custom/filter-sort/filter';
import { RECENTLY_DELETED_ID } from 'webapp/models-custom/filter-sort/filter';
import type PopupContext from 'webapp/models-custom/popup/context';
import type PopupList from 'webapp/models-custom/popup/popup-list';

export default class FilterLineComponent extends FilterLineOrButtonComponent {
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare boardsService: Service.ModelManager.Boards;

	searchDelay = 400;

	get showSearch(): boolean {
		return !this.args.model.hideSearch;
	}

	// -------- View options --------

	get showViewSelector(): boolean {
		return !this.args.model.hideViewSelector && this.args.model.viewOptions.length > 0;
	}

	closeFilterPopups(): void {
		this.popupService.popupStack.forEach((popupModel) => {
			if (popupModel.isContext && popupModel.popupClass?.includes('filters')) {
				void popupModel.close();
			}
		});
	}

	@action
	openFilterDropdown(filter: AnyFilter<FilterIdType>): void {
		if (!this.node) {
			return;
		}

		const filterId = filter.id,
			attachTo = this.node.querySelector<HTMLElement>(
				`.filterSwitcher.f${filterId} a ui-icon`,
			);

		if (!attachTo) {
			return;
		}

		const existingPopup = this.popupService.findContextByComponentAndAttachTo(
			CommonPopupsPopupListComponent,
			attachTo,
		);

		if (existingPopup?.isOpen) {
			return;
		}

		// close any existing filter popup if there's one open
		if (this.isPopupOpen) {
			this.closeFilterPopups();
			// small delay to allow popup positioning to recalculate after previous popup closes
			later(() => this.createAndOpenPopup(filter, attachTo), 200);
			return;
		}

		this.createAndOpenPopup(filter, attachTo);
	}

	private createAndOpenPopup(filter: AnyFilter<FilterIdType>, attachTo: HTMLElement): void {
		let popup: PopupContext | undefined;

		if (filter.options.length) {
			const popupClasses = `filterLine filters ${filter.extraClass}`;

			if (filter.customCreatePopupFn) {
				// it also opens the popup
				popup = filter.customCreatePopupFn(
					attachTo,
					popupClasses,
					this.args.theme as Theme,
				);
			} else {
				popup = this.popupService.createListPopup(
					attachTo,
					this.theme,
					popupClasses,
					false,
					true,
				);
			}
		} else {
			if (filter.isTag) {
				popup = this.popupService.createContext(TagPopupEmptyComponent, attachTo, {
					theme: this.theme,
					popupClass: 'emptytagspopup',
				});
			}
		}

		if (popup) {
			let itemsList = this.parseFilterOptionsForPopup(filter);

			// Store original order for multiselect repositioning
			if (filter.isMultiSelect) {
				this.originalFilterOrder.set(filter.id.toString(), [...itemsList]);
				itemsList = this.sortMultiSelectItemsBySelection(itemsList);
			}

			if (!filter.customCreatePopupFn) {
				(popup as PopupList).open({
					itemsList: itemsList,
					searchableProperties:
						filter.searchableProperties as (keyof PopupListItem<string>)[],
					skipCloseOnAction: true, // we handle closing manually in doAction
					onClearSelection: filter.shouldShowClearSelection
						? () => this.clearMultiSelectFilterSelection(filter, popup)
						: undefined,
					doAction: (_actionName: string, rawId: string) => {
						const id = rawId as number | string;
						filter.selectOption(id);

						const shouldClose = filter.shouldClosePopupOnSelection(id);

						// close popup if option is not combinable or if it's a single-select filter
						if (shouldClose && popup.isOpen) {
							void popup.close();
						}
						// update popup items to reflect new selection state for multiselect
						else if (filter.isMultiSelect) {
							this.updateMultiSelectPopupData(popup, filter, id);
						}
					},
				});

				this.isPopupOpen = true;
				popup.on(PopupEvent.CLOSED, this, () => {
					if (isAlive(this)) {
						this.isPopupOpen = false;
					}
				});
			}
		}
	}

	@action
	searchChanged(value: string): void {
		this.args.model.typedFilter = value;
	}

	@action
	onIsActiveChange(isActive: boolean): void {
		this.navigationService.isFilterSearchActive = isActive;
	}

	@cached
	get viewOptions() {
		const isDeletedFilterSelected = this.args.model.filters.some((filter) =>
			filter.selected.includes(RECENTLY_DELETED_ID),
		);

		return (this.args.model.viewOptions || [])
			.filter((item) => !(isDeletedFilterSelected && item.id === 'flow'))
			.map((item) => {
				return {
					value: item.id,
					label: '',
					disabled: false,
					icon: item.icon,
				};
			});
	}

	get selectedViewOption() {
		const viewId = this.args.model.viewId;
		return this.viewOptions.some((opt) => opt.value === viewId)
			? viewId
			: this.viewOptions[0]?.value;
	}

	@action
	changeViewOption(option: AnySimpleValue) {
		if (option === 'flow') {
			this.boardsService.clearFlowViewSelectedBoardId();
		}

		this.args.model.openView(option as string);
	}

	// --------- MULTI-SELECT START ---------

	// store original order for multiselect repositioning
	private originalFilterOrder = new Map<string, PopupListItem<FilterIdType>[]>();

	private isItemSelected<T>(item: PopupListItem<T>): boolean {
		return item.class?.includes('selected') ?? false;
	}

	private updateCurrentListWithFreshStates(
		filter: AnyFilter<FilterIdType>,
		currentList: PopupListItem<FilterIdType>[],
		unselectedId?: number | string,
	): PopupListItem<FilterIdType>[] {
		const freshOptions = this.parseFilterOptionsForPopup(filter);
		return currentList.map((currentItem) => {
			const freshItem = freshOptions.find((fresh) => fresh.id === currentItem.id);
			return freshItem
				? {
						...freshItem,
						class:
							unselectedId === currentItem.id
								? freshItem.class
								: currentItem.class?.includes('selected-section')
									? `${freshItem.class} selected-section`
									: freshItem.class,
					}
				: currentItem;
		});
	}

	private sortMultiSelectItemsBySelection<FilterIdType>(
		items: PopupListItem<FilterIdType>[],
	): PopupListItem<FilterIdType>[] {
		// separate selected and unselected items, maintaining order within each group
		const selectedItems = items
			.filter((item) => this.isItemSelected(item))
			.map((item) => {
				return {
					...item,
					class: `${item.class} selected-section`,
				};
			});
		const unselectedItems = items.filter(
			(item) => item.allowDuplicateSelection || !this.isItemSelected(item),
		);

		return [...selectedItems, ...unselectedItems];
	}

	private moveUnselectedItemToOriginalPosition(
		filter: AnyFilter<FilterIdType>,
		currentList: PopupListItem<FilterIdType>[],
		unselectedId: number | string,
	): PopupListItem<FilterIdType>[] {
		const originalOrder = this.originalFilterOrder.get(filter.id.toString()) || [];

		// update all items with fresh selection states
		const updatedCurrentList = this.updateCurrentListWithFreshStates(
			filter,
			currentList,
			unselectedId,
		);

		// find the unselected item and remove it from current position
		const unselectedItemIndex = updatedCurrentList.findIndex(
			(item) => item.id === unselectedId,
		);
		if (unselectedItemIndex === -1) {
			return updatedCurrentList;
		}

		const unselectedItem = updatedCurrentList[unselectedItemIndex]!;

		// remove ALL occurrences of the unselected item to prevent duplication
		const listWithoutUnselected = updatedCurrentList.filter((item) => item.id !== unselectedId);

		// find the top section (consecutive selected items from the beginning) to ignore
		let topSectionEnd = 0;
		while (
			topSectionEnd < listWithoutUnselected.length &&
			this.isItemSelected(listWithoutUnselected[topSectionEnd]!)
		) {
			topSectionEnd++;
		}

		// find the correct insertion index based on original order, but only in the non-top section
		let insertIndex = listWithoutUnselected.length; // default to end

		for (let i = 0; i < originalOrder.length; i++) {
			if (originalOrder[i]!.id === unselectedId) {
				// look for the next item that appears after this one in original order (top section is already ignored)
				for (let j = i + 1; j < originalOrder.length; j++) {
					const nextOriginalItem = originalOrder[j]!;
					const nextItemIndex = listWithoutUnselected.findIndex(
						(item, index) => index >= topSectionEnd && item.id === nextOriginalItem.id,
					);

					if (nextItemIndex !== -1) {
						insertIndex = nextItemIndex;
						break;
					}
				}
				break;
			}
		}

		// ensure we don't insert in the top section
		if (insertIndex < topSectionEnd) {
			insertIndex = topSectionEnd;
		}

		// insert the unselected item at the correct position
		listWithoutUnselected.splice(insertIndex, 0, unselectedItem);
		return listWithoutUnselected;
	}

	private parseMultiSelectFilterOptionsInCurrentOrder(
		filter: AnyFilter<FilterIdType>,
		currentList: PopupListItem<FilterIdType>[],
	): PopupListItem<FilterIdType>[] {
		if (!filter.isMultiSelect) {
			return this.parseFilterOptionsForPopup(filter);
		}

		// create a fresh list from parseFilterOptionsForPopup but in the current popup order
		return this.updateCurrentListWithFreshStates(filter, currentList);
	}

	private updateMultiSelectPopupData(
		popup: PopupContext,
		filter: AnyFilter<FilterIdType>,
		clickedId?: number | string,
	): void {
		if (!filter.isMultiSelect) {
			return;
		}

		const popupData = popup.data as { itemsList?: PopupListItem<FilterIdType>[] }; // a list popup
		if (popup.isOpen && popupData.itemsList) {
			const currentList = popupData.itemsList;

			if (!currentList) {
				return;
			}

			// check if an item was clicked and was selected before
			const wasSelected =
				clickedId !== undefined &&
				currentList.find(
					(item: PopupListItem<FilterIdType>) =>
						item.id === clickedId && this.isItemSelected(item),
				);

			let updatedList: PopupListItem<FilterIdType>[];

			if (wasSelected && clickedId !== undefined) {
				// if unselecting, move just this item to its correct position relative to other unselected items
				updatedList = this.moveUnselectedItemToOriginalPosition(
					filter,
					currentList,
					clickedId,
				);
			} else {
				// if selecting, maintain current item order in popup but with fresh selection states
				updatedList = this.parseMultiSelectFilterOptionsInCurrentOrder(filter, currentList);
			}

			// force a new object reference to trigger reactivity
			const newData = {
				...popup.data,
				itemsList: updatedList,
				onClearSelection: filter.shouldShowClearSelection
					? () => this.clearMultiSelectFilterSelection(filter, popup)
					: undefined,
			};
			popup.data = newData;
		}
	}

	private clearMultiSelectFilterSelection(
		filter: AnyFilter<FilterIdType>,
		popup: PopupContext,
	): void {
		if (!filter.isMultiSelect) {
			return;
		}

		filter.selectOption(filter.defaultValue);

		// update popup to reflect new selection state
		if (popup.isOpen && (popup.data as { itemsList?: unknown }).itemsList) {
			const updatedList = this.parseFilterOptionsForPopup(filter);
			const sortedList = this.sortMultiSelectItemsBySelection(updatedList);

			// Force a new object reference to trigger reactivity
			const newData = {
				...popup.data,
				itemsList: sortedList,
				onClearSelection: undefined, // hide clear button since only default is selected
			};
			// Reassign to trigger @tracked reactivity
			popup.data = newData;
			// Force component to re-evaluate by triggering reposition
			popup.reposition();
		}
	}

	// --------- MULTI-SELECT END ---------
	<template>
		<div
			class={{joinClasses
				(array
					"filter-line"
					(if this.showFilters "has-filters")
					(if this.isSearchActive "search-active")
				)
			}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{searchKeypress}}
		>
			<div class="filter-line__left">
				{{#if this.showSearch}}
					<FormInputSearchComponent
						@value={{@model.typedFilter}}
						@searchDelay={{this.searchDelay}}
						@placeholder={{@model.searchPlaceholder}}
						@onChange={{this.searchChanged}}
						@onIsActiveChange={{this.onIsActiveChange}}
						@theme={{this.theme}}
					/>
				{{/if}}
			</div>

			<div class="filter-line__right">
				{{yield}}

				{{#if this.showFilters}}

					{{#if @showJoinedFilterSwitchers}}

						<FilterButtonComponent @model={{@model}} />

					{{else}}

						<div class="filterSwitchers">

							{{#each @model.filters as |filter index|}}
								<FilterLineItemComponent
									@model={{filter}}
									@isSortBySearchPointsArray={{get
										this.isSortBySearchPointsArray
										index
									}}
									@theme={{this.theme}}
									@onOpenFilterDropdown={{fn this.openFilterDropdown filter}}
								/>
							{{/each}}

						</div>

					{{/if}}
				{{/if}}

				{{#if this.showViewSelector}}
					<FormRadiobuttonGroupComponent
						class="radiobutton-switcher radiobutton-switcher--icons-only"
						@selected={{this.selectedViewOption}}
						@options={{this.viewOptions}}
						@onChange={{this.changeViewOption}}
						@theme="alternate"
					/>
				{{/if}}
			</div>

		</div>
	</template>
}
